import React, { useState, useEffect } from 'react';
import UploadBox from './UploadBox';
import UploadProgress from './UploadProgress';
import { Upload, Eye, X } from 'lucide-react';

const PreviewModal = ({ file, onClose }) => {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
  }, [file]);

  if (!file || !url) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 md:p-8" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div>
            <h3 className="font-bold text-gray-800 text-lg">Document Preview</h3>
            <p className="text-sm text-gray-500 truncate max-w-md">{file.name}</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="flex-1 overflow-auto bg-gray-100/50 p-4 flex justify-center">
          {file.type.startsWith('image/') ? (
             <img src={url} alt="Preview" className="max-w-full max-h-full object-contain rounded shadow-sm bg-white" />
          ) : file.type === 'application/pdf' ? (
             <object data={url} type="application/pdf" className="w-full h-full rounded shadow-sm bg-white">
               <div className="flex flex-col items-center justify-center h-full bg-white text-gray-500">
                 <p>PDF Preview not natively supported in this browser.</p>
                 <a href={url} target="_blank" rel="noreferrer" className="mt-2 text-blue-500 hover:underline">Click here to open it</a>
               </div>
             </object>
          ) : (
             <div className="flex items-center justify-center h-full text-gray-500 bg-white w-full rounded shadow-sm">
               <p>Live visual preview is not available for this file type.</p>
             </div>
          )}
        </div>
      </div>
    </div>
  );
};

const UploadCard = ({
  selectedFile,
  onFileSelect,
  onRemoveFile,
  onUpload,
  uploadProgress,
  uploadStatus,
  errorMessage
}) => {
  const [showPreview, setShowPreview] = useState(false);

  return (
    <>
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 md:p-8 relative z-10">
        <div className="mb-6 pb-4 border-b border-gray-50">
          <h2 className="text-xl font-bold text-gray-800">Document Upload</h2>
          <p className="text-sm text-gray-500 mt-1">
            Upload your purchase order document to automatically extract and process the data.
          </p>
        </div>

        <UploadBox 
          selectedFile={selectedFile}
          onFileSelect={onFileSelect}
          onRemoveFile={onRemoveFile}
        />

        <UploadProgress 
          progress={uploadProgress}
          status={uploadStatus}
          errorMessage={errorMessage}
        />

        <div className="mt-8 flex justify-end pt-4 border-t border-gray-50 space-x-3">
          {selectedFile && (
            <button
              type="button"
              onClick={() => setShowPreview(true)}
              disabled={uploadStatus === 'uploading'}
              className={`flex items-center px-6 py-2.5 rounded-lg font-medium transition-all duration-200
                ${uploadStatus === 'uploading' 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 shadow-sm hover:shadow'}`}
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview Document
            </button>
          )}

          <button
            onClick={onUpload}
            disabled={!selectedFile || uploadStatus === 'uploading'}
            className={`flex items-center px-6 py-2.5 rounded-lg font-medium transition-all duration-200
              ${(!selectedFile || uploadStatus === 'uploading') 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg active:transform active:scale-95'}`}
          >
            <Upload className={`w-4 h-4 mr-2 ${uploadStatus === 'uploading' ? 'animate-bounce' : ''}`} />
            {uploadStatus === 'uploading' ? 'Uploading...' : 'Upload & Extract'}
          </button>
        </div>
      </div>

      {showPreview && selectedFile && (
        <PreviewModal 
          file={selectedFile} 
          onClose={() => setShowPreview(false)} 
        />
      )}
    </>
  );
};

export default UploadCard;
